import React, { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { useScreenClass } from '@vp/swan'

import { scrollToElement } from '../../../../utils/ScrollHelpers'

export interface ICarouselScrollContext {
  colorsCarouselRef: (node: HTMLElement | null) => void
  colorsCarouselInView: boolean
  colorsCarouselMounted: boolean
  colorsHeaderRef: (node: HTMLElement | null) => void
  colorsHeaderInView: boolean
  colorsHeaderMounted: boolean
  relatedProductsCarouselRef: (node: HTMLElement | null) => void
  relatedProductsCarouselInView: boolean
  relatedProductsCarouselMounted: boolean
  relatedProductsHeaderRef: (node: HTMLElement | null) => void
  relatedProductsHeaderInView: boolean
  relatedProductsHeaderMounted: boolean
  scrollTo: (key: 'colorsCarousel' | 'relatedProductsCarousel', scrollPosition?: ScrollLogicalPosition) => void
}

export const CarouselScrollContext = createContext<ICarouselScrollContext>({} as ICarouselScrollContext)

const CarouselScrollContextProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const screenClass = useScreenClass()
  const colorsCarouselRef = useRef<HTMLElement | null>(null)
  const relatedProductsCarouselRef = useRef<HTMLElement | null>(null)

  // Shrink viewport from bottom by footer height (110px) so elements are considered
  // "out of view" when they reach the footer area.
  // On mobile (xs), don't apply rootMargin since the link is part of the footer, not sticky
  // Memoize to prevent IntersectionObserver from constantly recreating
  const rootMargin = useMemo(() => (screenClass === 'xs' ? undefined : '0px 0px -110px 0px'), [screenClass])

  const { ref: colorsObserverRef, inView: colorsRefInView } = useInView({
    threshold: 1,
    rootMargin,
  })
  const { ref: relatedProductsObserverRef, inView: relatedProductsCarouselInView } = useInView({
    threshold: 1,
    rootMargin,
  })
  const {
    ref: colorsHeaderRef,
    inView: colorsHeaderInView,
    entry: colorsHeaderEntry,
  } = useInView({
    threshold: 1,
    rootMargin,
  })
  const {
    ref: relatedProductsHeaderRef,
    inView: relatedProductsHeaderInView,
    entry: relatedProductsHeaderEntry,
  } = useInView({
    threshold: 1,
    rootMargin,
  })

  const [colorsCarouselMounted, setColorsCarouselMounted] = useState<boolean>(false)
  const [relatedProductsCarouselMounted, setRelatedProductsCarouselMounted] = useState<boolean>(false)
  const [colorsHeaderMounted, setColorsHeaderMounted] = useState<boolean>(false)
  const [relatedProductsHeaderMounted, setRelatedProductsHeaderMounted] = useState<boolean>(false)

  useEffect(() => {
    if (colorsHeaderEntry) {
      setColorsHeaderMounted(Boolean(colorsHeaderEntry))
    }
  }, [colorsHeaderEntry])

  useEffect(() => {
    if (relatedProductsHeaderEntry) {
      setRelatedProductsHeaderMounted(Boolean(relatedProductsHeaderEntry))
    }
  }, [relatedProductsHeaderEntry])

  const scrollTo = useCallback((key: 'colorsCarousel' | 'relatedProductsCarousel', scrollPosition?: ScrollLogicalPosition) => {
    const target = key === 'colorsCarousel' ? colorsCarouselRef.current : relatedProductsCarouselRef.current
    if (target) scrollToElement(target, scrollPosition ?? 'end')
  }, [])

  const colorsCarouselRefCallback = useCallback(
    (node: HTMLElement | null) => {
      colorsCarouselRef.current = node
      setColorsCarouselMounted(!!node)
      if (node) {
        colorsObserverRef(node)
      }
    },
    [colorsObserverRef],
  )

  const relatedProductsCarouselRefCallback = useCallback(
    (node: HTMLElement | null) => {
      relatedProductsCarouselRef.current = node
      setRelatedProductsCarouselMounted(!!node)
      if (node) {
        relatedProductsObserverRef(node)
      }
    },
    [relatedProductsObserverRef],
  )

  const contextValue = useMemo(
    () => ({
      colorsCarouselRef: colorsCarouselRefCallback,
      colorsCarouselInView: colorsRefInView,
      colorsCarouselMounted,
      colorsHeaderRef: colorsHeaderRef,
      colorsHeaderInView: colorsHeaderInView,
      colorsHeaderMounted,
      relatedProductsCarouselRef: relatedProductsCarouselRefCallback,
      relatedProductsCarouselInView: relatedProductsCarouselInView,
      relatedProductsCarouselMounted,
      relatedProductsHeaderRef: relatedProductsHeaderRef,
      relatedProductsHeaderInView: relatedProductsHeaderInView,
      relatedProductsHeaderMounted,
      scrollTo,
    }),
    [
      colorsCarouselRefCallback,
      colorsRefInView,
      colorsCarouselMounted,
      colorsHeaderRef,
      colorsHeaderInView,
      colorsHeaderMounted,
      relatedProductsCarouselRefCallback,
      relatedProductsCarouselInView,
      relatedProductsCarouselMounted,
      relatedProductsHeaderRef,
      relatedProductsHeaderInView,
      relatedProductsHeaderMounted,
      scrollTo,
    ],
  )

  return <CarouselScrollContext.Provider value={contextValue}>{children}</CarouselScrollContext.Provider>
}

export default CarouselScrollContextProvider
